import React, { FC, useCallback, useContext, useEffect, useState } from 'react'
import { makeStyles } from '@mui/styles'
import { Grid, Typography, Theme, Tooltip } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { useHistory, useLocation } from 'react-router-dom'
import { endOfDay, startOfDay } from 'date-fns'
import HelpIcon from '@mui/icons-material/HelpOutline'
import { DataPicker } from '../../components/DatePicker'
import { Search } from '../../components/Search'
import { GreenButton } from '../../components/Buttons/GreenButton'
import { routes } from '../../constants/routes'
import { icons } from '../../constants/icons'
import { TableComponent } from '../../components/Table'
import { columns } from './companiesList'
import { AppStateContext, useRefresh, useRest } from '../../components/Context'
import useDebounce from '../../hooks/useDebounce'
import { UploadCsvFile } from '../UploadCsvFile'
import { generateParams } from '../../utils'
import { tabList } from '../CompanyProfile/CompanyProfileTabs/tabList'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    width: '100%',
    paddingTop: theme.spacing(4),
    '& p': {
      display: 'none',
    },
  },
  topWrap: {
    width: '100%',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  rightSide: {
    flexWrap: 'nowrap',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    width: 'max-content',
    paddingTop: theme.spacing(2),
    '& :first-child': {
      marginLeft: 0,
    },
  },
  title: {
    paddingTop: theme.spacing(2),
  },
  button: {
    paddingLeft: 15,
  },
  table: {
    paddingTop: theme.spacing(2),
  },
  tooltipWrap: {
    position: 'relative',
  },
  helpButton: {
    fontSize: 18,
    color: 'rgba(255,255,255,0.6)',
    '& svg': {
      fontSize: 18,
    },
  },
  tooltip: {
    position: 'absolute',
    top: -7,
    right: 7,
  },
}))

export const Companies: FC = () => {
  const classes = useStyles()
  const history = useHistory()
  const { rxios } = useRest()
  const { saveIsRefresh } = useRefresh()
  const { getIsRefresh } = useContext(AppStateContext)
  const formMethods = useForm({
    mode: 'onBlur',
  })
  const { watch, getValues } = formMethods

  const [loading, setLoading] = useState<boolean>(false)
  const [isInitDate, setInitDate] = useState<boolean>(false)
  // eslint-disable-next-line
  const [values, setValues] = useState<any>({})
  // eslint-disable-next-line
  const [startDateSelected, setStartDateSelected] = useState<any>('')
  // eslint-disable-next-line
  const [endDateSelected, setEndDateSelected] = useState<any>('')
  const [queryString, setQueryString] = useState<string>('')
  const [searchDebounce, setSearchDebounce] = useState<string>('')
  const debouncedValue = useDebounce<string>(queryString, 200)
  const [refresh, setRefresh] = useState<boolean | undefined>()

  useEffect(() => setRefresh(getIsRefresh()), [setRefresh, getIsRefresh])

  const { search } = useLocation()
  const stringParams = new URLSearchParams(search)
  const pageUrl: number | string | null = stringParams.get('page')
  // eslint-disable-next-line
  useEffect(() => setSearchDebounce(queryString), [debouncedValue])

  const getCompanyId = useCallback(
    (companyId) => {
      history.push(`${routes.COMPANIES}/${companyId}/${tabList[0].slug}`)
    },
    [history],
  )

  const pushParamsToURL = useCallback(
    (startDate, endDate, searchString: string) => {
      const currentUrlParams = new URLSearchParams(window.location.search)

      if (startDate) {
        currentUrlParams.set('startDate', startDate)
        history.push(`${window.location.pathname}?${currentUrlParams}`)
      } else {
        currentUrlParams.delete('startDate')
        history.push(`${window.location.pathname}?${currentUrlParams}`)
      }

      if (endDate) {
        currentUrlParams.set('endDate', endDate)
        history.push(`${window.location.pathname}?${currentUrlParams}`)
      } else {
        currentUrlParams.delete('endDate')
        history.push(`${window.location.pathname}?${currentUrlParams}`)
      }

      if (searchString) {
        currentUrlParams.set('search', searchString)
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      } else {
        currentUrlParams.delete('search')
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      }
    },
    [history],
  )

  const loadData = useCallback(
    async (page, startDate = '', endDate = '', searchString = '') => {
      let cleanupFunction = false
      setLoading(true)

      rxios
        .get(
          `companies?${generateParams({
            page,
            count: '10',
            startDate,
            endDate,
            search: encodeURIComponent(searchString.toUpperCase()),
          })}`,
        )
        .subscribe(
          // eslint-disable-next-line
          (next: any) => {
            setValues(next)
            history.push(`companies?page=${page}`)
            pushParamsToURL(startDate, endDate, searchString)

            if (!cleanupFunction) setLoading(false)
          },
          (error) => {
            pushParamsToURL(startDate, endDate, searchString)
            toast.error(error)
            setLoading(false)
          },
          () => {
            setLoading(false)
          },
        )
      cleanupFunction = true
    },
    // eslint-disable-next-line
    [history, rxios],
  )

  const initData = useCallback(
    async (page) => {
      let cleanupFunction = false
      setLoading(true)
      rxios.get(`companies?page=${page}&count=10`).subscribe(
        // eslint-disable-next-line
        (next: any) => {
          setValues(next)
          if (!cleanupFunction) setLoading(false)
          setInitDate(true)
          saveIsRefresh(false)
        },
        (error) => {
          toast.error(error)
          setLoading(false)
        },
        () => {
          setLoading(false)
        },
      )
      cleanupFunction = true
    },
    // eslint-disable-next-line
    [],
  )

  useEffect(() => {
    ;(async () => {
      await initData(pageUrl || '1')
    })()
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (refresh) {
      ;(async () => {
        await initData(pageUrl || '1')
      })()
    }
    // eslint-disable-next-line
  }, [refresh])

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'startDate') setStartDateSelected(value?.startDate?.toISOString())
      if (name === 'endDate') setEndDateSelected(value?.endDate?.toISOString())
      if (name === 'search') setQueryString(value?.[name])
    })
    return () => subscription.unsubscribe()
  }, [watch])

  useEffect(() => {
    if (isInitDate) {
      setEndDateSelected(endDateSelected === '1970-01-01' ? '' : endDateSelected)
      ;(async () => {
        await loadData(1, startDateSelected || '', endDateSelected || '', searchDebounce || '')
      })()
    }
    // eslint-disable-next-line
  }, [startDateSelected, endDateSelected, searchDebounce])

  return (
    <Grid className={classes.container}>
      <Grid container className={classes.topWrap}>
        <Typography variant="h1" className={classes.title}>
          Companies
        </Typography>
        <FormProvider {...formMethods}>
          <Grid
            container
            direction={'row'}
            justifyContent={'flex-end'}
            alignItems={'center'}
            className={classes.rightSide}
          >
            <DataPicker
              placeholder={'Start Date'}
              margin={'0 8px'}
              dataPickerName={'startDate'}
              required={false}
              maxDate={getValues().endDate}
              formatValue={startOfDay}
            />
            <DataPicker
              placeholder={'End Date'}
              margin={'0 8px'}
              dataPickerName={'endDate'}
              required={false}
              minDate={getValues().startDate}
              formatValue={endOfDay}
            />
            <Search inputName={'search'} />
            <Grid className={classes.tooltipWrap}>
              <UploadCsvFile type={'company'} />
              <Tooltip
                className={classes.tooltip}
                title={
                  <>
                    <Typography>CSV file structure for companies uploading:</Typography>
                    <p>Email, Company Name, Phone Number, Country, City, Person of Contact, Currency</p>
                  </>
                }
              >
                <HelpIcon className={classes.helpButton} />
              </Tooltip>
            </Grid>
            <GreenButton width={120} prefix={icons.ADD} ml={8} text={'Create'} href={`${routes.COMPANIES}/create`} />
          </Grid>
        </FormProvider>
      </Grid>
      <Grid container className={classes.table}>
        <TableComponent
          getElementId={getCompanyId}
          columns={columns}
          data={values.items}
          params={values.meta}
          loadData={(pageNumber: number) =>
            loadData(pageNumber, startDateSelected || '', endDateSelected || '', searchDebounce || '')
          }
          isShowEditIcon={false}
          isShowInfoIcon={true}
          loading={loading}
        />
      </Grid>
    </Grid>
  )
}
